import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { IObservationsFilterOptions } from '@app/interfaces/observation';
import { tableDataInit } from '@app/pages/FishingHabitat/Observations/conts';
import { formatFromFilter, formatToFilter } from '@app/utils/formatDate';
import { getUpdatedOptions } from '@app/utils/pageFilters';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

interface ObservationsFilterState {
  filterOptions: IObservationsFilterOptions;
  shouldTriggerFetch: boolean;
  searchText: string;
  selectedOrganizationalEntityIds: FilterIdsType;
  selectedWaterbodyIds: FilterIdsType;
  selectedInspectorIds: FilterIdsType;
  selectedCategoryIds: FilterIdsType;
  fromFilterTemp: Dayjs | null | undefined;
  toFilterTemp: Dayjs | null | undefined;
  selectedOrganizationalEntityIdsTemp: FilterIdsType;
  selectedWaterbodyIdsTemp: FilterIdsType;
  selectedInspectorIdsTemp: FilterIdsType;
  selectedCategoryIdsTemp: FilterIdsType;
}

const initialState: ObservationsFilterState = {
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: false,
  searchText: '',
  selectedOrganizationalEntityIds: [],
  selectedWaterbodyIds: [],
  selectedInspectorIds: [],
  selectedCategoryIds: [],
  fromFilterTemp: undefined,
  toFilterTemp: undefined,
  selectedOrganizationalEntityIdsTemp: [],
  selectedWaterbodyIdsTemp: [],
  selectedInspectorIdsTemp: [],
  selectedCategoryIdsTemp: [],
};

const observationsFiltersSlice = createSlice({
  name: 'observationsFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<IObservationsFilterOptions>) {
      state.filterOptions = action.payload;

      const availableOrganizationalEntityIds = getUpdatedOptions(
        state.selectedOrganizationalEntityIdsTemp,
        action.payload.organizationalEntities,
      );
      state.selectedOrganizationalEntityIdsTemp = availableOrganizationalEntityIds;
      state.selectedOrganizationalEntityIds = availableOrganizationalEntityIds;

      const availableWaterbodyIds = getUpdatedOptions(state.selectedWaterbodyIdsTemp, action.payload.waterbodies);
      state.selectedWaterbodyIdsTemp = availableWaterbodyIds;
      state.selectedWaterbodyIds = availableWaterbodyIds;

      const availableInspectorIds = getUpdatedOptions(state.selectedInspectorIdsTemp, action.payload.inspectors);
      state.selectedInspectorIdsTemp = availableInspectorIds;
      state.selectedInspectorIds = availableInspectorIds;

      const availableCategoryIds = getUpdatedOptions(state.selectedCategoryIdsTemp, action.payload.categories);
      state.selectedCategoryIdsTemp = availableCategoryIds;
      state.selectedCategoryIds = availableCategoryIds;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setSelectedOrganizationalEntityIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIds = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedInspectorIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedInspectorIds = action.payload;
    },
    setSelectedCategoryIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedCategoryIds = action.payload;
    },
    setFromFilterTemp(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromFilterTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToFilterTemp(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toFilterTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedOrganizationalEntityIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIdsTemp = action.payload;
    },
    setSelectedWaterbodyIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIdsTemp = action.payload;
    },
    setSelectedInspectorIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedInspectorIdsTemp = action.payload;
    },
    setSelectedCategoryIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedCategoryIdsTemp = action.payload;
    },
    resetFilters(state) {
      state.searchText = initialState.searchText;
      state.selectedOrganizationalEntityIds = initialState.selectedOrganizationalEntityIds;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedInspectorIds = initialState.selectedInspectorIds;
      state.selectedCategoryIds = initialState.selectedCategoryIds;
      state.fromFilterTemp = initialState.fromFilterTemp;
      state.toFilterTemp = initialState.toFilterTemp;
      state.selectedOrganizationalEntityIdsTemp = initialState.selectedOrganizationalEntityIdsTemp;
      state.selectedWaterbodyIdsTemp = initialState.selectedWaterbodyIdsTemp;
      state.selectedInspectorIdsTemp = initialState.selectedInspectorIdsTemp;
      state.selectedCategoryIdsTemp = initialState.selectedCategoryIdsTemp;
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setSelectedOrganizationalEntityIds,
  setSelectedWaterbodyIds,
  setSelectedInspectorIds,
  setSelectedCategoryIds,
  setFromFilterTemp,
  setToFilterTemp,
  setSelectedOrganizationalEntityIdsTemp,
  setSelectedWaterbodyIdsTemp,
  setSelectedInspectorIdsTemp,
  setSelectedCategoryIdsTemp,
  resetFilters,
} = observationsFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { observationsFilters: ObservationsFilterState }) => {
  const {
    searchText,
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedCategoryIds,
    fromFilterTemp,
    toFilterTemp,
  } = state.observationsFilters;

  return (
    !!searchText.length ||
    !!selectedOrganizationalEntityIds.length ||
    !!selectedWaterbodyIds.length ||
    !!selectedInspectorIds.length ||
    !!selectedCategoryIds.length ||
    !!fromFilterTemp ||
    !!toFilterTemp
  );
};

export const activeFiltersCountSelector = (state: { observationsFilters: ObservationsFilterState }) => {
  const {
    searchText,
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedCategoryIds,
    fromFilterTemp,
    toFilterTemp,
  } = state.observationsFilters;

  let count = 0;
  if (searchText.length > 0) count++;
  if (selectedOrganizationalEntityIds.length > 0) count++;
  if (selectedWaterbodyIds.length > 0) count++;
  if (selectedInspectorIds.length > 0) count++;
  if (selectedCategoryIds.length > 0) count++;
  if (fromFilterTemp) count++;
  if (toFilterTemp) count++;

  return count;
};

export const fetchParamsSelector = (state: { observationsFilters: ObservationsFilterState }) => {
  const {
    searchText,
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedCategoryIds,
    fromFilterTemp,
    toFilterTemp,
  } = state.observationsFilters;

  const fromFilterString = fromFilterTemp ? formatFromFilter(fromFilterTemp) : '';
  const toFilterString = toFilterTemp ? formatToFilter(toFilterTemp) : '';

  return {
    searchText,
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedCategoryIds,
    fromFilterString,
    toFilterString,
  };
};

export const observationsFiltersActions = observationsFiltersSlice.actions;

export const observationsFilters = observationsFiltersSlice.reducer;
